import React, { useMemo, useState } from 'react';

import {
  autoUpdate,
  FloatingFocusManager,
  offset,
  size,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { flip } from '@floating-ui/react-dom';
import classNames from 'classnames';

import { mod } from '@travauxlib/shared/src/utils/math';

import { ItemType } from './type';
import { itemMatcher } from './utils/itemMatcher';

import type { ActionListItem } from './type';
import type { Placement } from '@floating-ui/react';

type ReferenceProps = React.HTMLAttributes<HTMLElement> & {
  ref: (element: HTMLElement | null) => void;
};

export type TriggerParams = {
  toggle: () => void;
  open: () => void;
  close: () => void;
  referenceProps: ReferenceProps;
  isOpen: boolean;
  advanceMenuForward: () => void;
  advanceMenuBackward: () => void;
  validateMenu: () => void;
};

export type ActionListProps<T> = {
  items: ActionListItem<T>[];
  trigger: (triggerParams: TriggerParams) => React.ReactElement;
  position?: Placement;
  fixedFloating?: boolean;
  offsetX?: number;
  offsetY?: number;
  height?: number;
  width?: number;
  className?: string;
  itemsContainerClassName?: string;
  scrollable?: boolean;
  small?: boolean;
  rounded?: boolean;
  autofocus?: boolean;
  isFullWidth?: boolean;
  onClose?: () => void;
};

export function ActionList<T>({
  items,
  trigger,
  position = 'bottom-end',
  fixedFloating,
  height = 200,
  width,
  scrollable,
  small,
  rounded,
  className,
  itemsContainerClassName,
  offsetY,
  offsetX,
  autofocus = true,
  isFullWidth,
  onClose,
}: ActionListProps<T>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [menuIndex, setMenuIndex] = React.useState<number | null>(null);
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const isRoot = parentId === null;

  const findNextSelectableItemIndex = (startIndex?: number, step: number = 1): number => {
    for (let i = 1; i < items.length; i++) {
      const nextIndex = startIndex === undefined ? i - 1 : mod(startIndex + step * i, items.length);
      const nextItem = items[nextIndex];
      if (
        (nextItem.type === ItemType.Action ||
          nextItem.type === ItemType.Option ||
          nextItem.type === ItemType.NewEntry ||
          nextItem.type === ItemType.Checkbox ||
          nextItem.type === ItemType.CustomAction) &&
        !nextItem.disabled
      ) {
        return nextIndex;
      }
    }
    return startIndex || 0;
  };
  const findPreviousSelectableItemIndex = (startIndex: number = 0): number =>
    findNextSelectableItemIndex(startIndex, -1);

  const scrollMenuForward = (newMenuIndex: number): void => {
    const previousIndex = menuIndex || 0;
    const nbMainItems = items.filter(item => item.type !== ItemType.NewEntry).length;

    if (refs.floating.current) {
      let container = refs.floating.current.children[0] as HTMLElement;
      let item;
      if (newMenuIndex >= nbMainItems) {
        container = refs.floating.current.children[1] as HTMLElement;
        item = container.childNodes[newMenuIndex - nbMainItems] as HTMLElement;
      } else {
        item = container.childNodes[newMenuIndex] as HTMLElement;
      }

      const heightDifference =
        container.clientHeight +
        container.scrollTop -
        item.getBoundingClientRect().height -
        item.offsetTop;

      if (heightDifference < 0) {
        container.scrollTop -= heightDifference;
      } else if (newMenuIndex < previousIndex) {
        container.scrollTop = 0;
      }
    }
  };

  const advanceMenuForward = (): void => {
    const newMenuIndex = findNextSelectableItemIndex(menuIndex === null ? undefined : menuIndex);
    setMenuIndex(newMenuIndex);

    scrollMenuForward(newMenuIndex);
  };

  const scrollMenuBackward = (newMenuIndex: number): void => {
    const nbMainItems = items.filter(item => item.type !== ItemType.NewEntry).length;
    const nbControlItems = items.filter(item => item.type === ItemType.NewEntry).length;

    if (refs.floating.current) {
      let container = refs.floating.current.children[0] as HTMLElement;
      let item;
      if (newMenuIndex >= nbMainItems) {
        container = refs.floating.current.children[1] as HTMLElement;
        item = container.childNodes[newMenuIndex - nbMainItems] as HTMLElement;
      } else {
        item = container.childNodes[newMenuIndex] as HTMLElement;
      }

      const heightDifference = container.scrollTop - item.offsetTop;

      if (newMenuIndex === items.length - nbControlItems - 1) {
        container.scrollTop = container.scrollHeight;
      } else if (heightDifference > 0) {
        container.scrollTop -= heightDifference;
      }
    }
  };

  const advanceMenuBackward = (): void => {
    const newMenuIndex = findPreviousSelectableItemIndex(menuIndex || 0);
    setMenuIndex(newMenuIndex);

    scrollMenuBackward(newMenuIndex);
  };

  const validateMenu = (): void => {
    if (menuIndex !== null) {
      const selectedItem = items[menuIndex];
      switch (selectedItem.type) {
        case ItemType.NewEntry:
          selectedItem.onClick();
          break;
        case ItemType.Action:
          selectedItem.onClick();
          break;
        case ItemType.Option:
          selectedItem.onChange(!selectedItem.value);
          break;
        case ItemType.Checkbox:
          selectedItem.onChange?.(selectedItem.value);
          break;
        case ItemType.CustomAction:
          selectedItem.onClick?.();
          break;
      }
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    placement: position,
    strategy: fixedFloating ? 'fixed' : 'absolute',
    open: isOpen,
    middleware: [
      flip(),
      offset({
        mainAxis: offsetY || 0,
        crossAxis: offsetX || 0,
      }),
      ...(isFullWidth
        ? [
            size({
              apply({ rects, elements }) {
                Object.assign(elements.floating.style, {
                  width: `${rects.reference.width}px`,
                });
              },
            }),
          ]
        : []),
    ],
    onOpenChange: open => {
      onClose?.();
      return setIsOpen(open);
    },
    whileElementsMounted: autoUpdate,
  });

  const close = (): void => {
    setIsOpen(false);
    setMenuIndex(null);
  };
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'click',
  });
  const role = useRole(context);
  const toggleOpen = (): void => {
    setIsOpen(isOpen => !isOpen);
    setMenuIndex(null);
  };

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);
  const referenceProps: ReferenceProps = {
    ref: refs.setReference,
    ...getReferenceProps(),
  };

  const content = useMemo(
    () =>
      items
        .filter(item => item.type !== ItemType.NewEntry)
        .flatMap((item, index) => {
          const element = itemMatcher(item, close, small, index === menuIndex, index);
          if (!element) {
            return [];
          }
          return element;
        }),
    [items, menuIndex, small],
  );

  const controls = useMemo(() => {
    const controlsItems = items.filter(item => item.type === ItemType.NewEntry);
    return controlsItems.flatMap((item, index) => {
      const element = itemMatcher(
        item,
        close,
        small,
        index + (items.length - controlsItems.length) === menuIndex,
        index,
      );
      if (!element) {
        return [];
      }
      return [
        React.cloneElement(element, {
          'aria-selected': index + (items.length - controlsItems.length) === menuIndex,
        }),
      ];
    });
  }, [items, menuIndex, small]);

  const triggeredComponent = trigger({
    toggle: toggleOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    referenceProps,
    isOpen,
    advanceMenuForward,
    advanceMenuBackward,
    validateMenu,
  });

  if (!content.length && !controls.length) {
    return <>{triggeredComponent}</>;
  }

  const renderedList = (
    <div
      ref={refs.setFloating}
      {...getFloatingProps()}
      //eslint-disable-next-line
      style={{
        ...floatingStyles,
        width: 'initial',
      }}
      data-testid="open-menu"
      className={classNames(
        'absolute z-50 ctn-neutral-white border shadow-xs rounded-xxs py-xxs',
        className,
        {
          'rounded-xs!': rounded,
          'pb-0!': controls && controls.length > 0,
        },
      )}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.code === 'ArrowDown') {
          e.preventDefault();
          advanceMenuForward();
        }
        if (e.code === 'ArrowUp') {
          e.preventDefault();
          advanceMenuBackward();
        }
        if (e.code === 'Enter') {
          e.preventDefault();
          validateMenu();
        }
      }}
    >
      <div
        //eslint-disable-next-line
        style={{
          ...(scrollable ? { maxHeight: `${height}px` } : {}),
          ...(width ? { width: `${width}px` } : {}),
        }}
        className={classNames(
          { 'overflow-y-scroll overflow-x-hidden': scrollable },
          itemsContainerClassName,
        )}
      >
        {content}
      </div>
      {controls && controls.length > 0 && <div className="py-xs border-t">{controls}</div>}
    </div>
  );

  return (
    <>
      {triggeredComponent}
      {isOpen && autofocus && isRoot && (
        <FloatingFocusManager context={context} modal={false} returnFocus={true}>
          {renderedList}
        </FloatingFocusManager>
      )}
      {isOpen && !(autofocus && isRoot) && renderedList}
    </>
  );
}
